import { useState, useEffect, useCallback } from 'react';
import {
  FileBarChart,
  Download,
  Calendar,
  Play,
  Trash2,
  MoreHorizontal,
  Plus,
  Eye,
  Settings,
  FileText,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { DetailedReportModal, DetailedReportData } from '../components/reports/DetailedReportModal';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../components/ui/use-toast';
import { reportApi, ReportTemplate } from '../services/reportApi';
import { createAdminNotification } from '../services/notificationService';

interface Report extends ReportTemplate {
  lastGenerated: string;
}

/** Metrics that cannot be generated or downloaded yet; show toast and disable actions. */
const UNSUPPORTED_DOWNLOAD_METRICS = ['Investor Growth', 'Compliance Status'];

function reportHasUnsupportedMetrics(metrics: string[] | undefined): boolean {
  if (!metrics || metrics.length === 0) return false;
  return metrics.some((m) => UNSUPPORTED_DOWNLOAD_METRICS.includes(m));
}

function toReport(r: ReportTemplate): Report {
  return {
    ...r,
    lastGenerated: r.lastGenerated ?? '—',
  };
}

export default function ReportsPage() {
  const { toast } = useToast();
  const [reports, setReports] = useState<Report[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [editReportId, setEditReportId] = useState<string | null>(null);
  const [editInitialData, setEditInitialData] = useState<DetailedReportData | null>(null);
  const [generatingId, setGeneratingId] = useState<string | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [viewReportData, setViewReportData] = useState<ReportTemplate | null>(null);
  const [viewDetailsLoading, setViewDetailsLoading] = useState(false);

  const fetchReports = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        const data = await reportApi.list();
        setReports(data.map(toReport));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Please try again';
        toast({
          title: 'Failed to load reports',
          description: message,
          variant: 'destructive',
        });
        if (!silent) setReports([]);
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [toast]
  );

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  const handleAddReport = async (formData: DetailedReportData) => {
    try {
      await reportApi.create({
        name: formData.name,
        type: formData.type,
        description: formData.description,
        schedule: formData.schedule,
        format: formData.format,
        metrics: formData.metrics,
        assets: formData.assets,
        dateRange: formData.dateRange,
        recipients: formData.recipients,
        includeCharts: formData.includeCharts,
      });
      toast({
        title: 'Report created successfully',
        description: `${formData.name} - ${formData.schedule}`,
      });
      createAdminNotification({
        type: 'alert',
        title: 'Report template created',
        message: `"${formData.name}" (${formData.schedule}) was created.`,
        priority: 'low',
      });
      setIsCreateModalOpen(false);
      fetchReports();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again';
      toast({
        title: 'Failed to create report',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const handleUpdateReport = async (id: string, formData: DetailedReportData) => {
    try {
      await reportApi.update(id, {
        name: formData.name,
        type: formData.type,
        description: formData.description,
        schedule: formData.schedule,
        format: formData.format,
        metrics: formData.metrics,
        assets: formData.assets,
        dateRange: formData.dateRange,
        recipients: formData.recipients,
        includeCharts: formData.includeCharts,
      });
      toast({
        title: 'Report updated successfully',
        description: formData.name,
      });
      createAdminNotification({
        type: 'alert',
        title: 'Report template updated',
        message: `"${formData.name}" was updated.`,
        priority: 'low',
      });
      setEditReportId(null);
      setEditInitialData(null);
      fetchReports();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again';
      toast({
        title: 'Failed to update report',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const handleGenerateReport = async (report: Report) => {
    if (reportHasUnsupportedMetrics(report.metrics)) {
      toast({
        title: 'Generate not available',
        description:
          'Reports that include "Investor Growth" or "Compliance Status" cannot be generated or downloaded yet. Please edit the report and deselect these metrics.',
        variant: 'destructive',
      });
      return;
    }
    try {
      setGeneratingId(report.id);
      await reportApi.generate(report.id);
      toast({
        title: 'Report generated',
        description: `${report.name} data is ready. Use Download to get the file.`,
      });
      createAdminNotification({
        type: 'alert',
        title: 'Report generated',
        message: `"${report.name}" was generated successfully.`,
        priority: 'low',
      });
      fetchReports(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again';
      toast({
        title: 'Failed to generate report',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setGeneratingId(null);
    }
  };

  const getDownloadExtension = (format: string): string => {
    const f = (format || 'json').toLowerCase();
    if (f === 'pdf') return 'pdf';
    if (f === 'excel' || f === 'xlsx') return 'xlsx';
    if (f === 'csv') return 'csv';
    return 'json';
  };

  const handleDownloadReport = async (report: Report) => {
    if (reportHasUnsupportedMetrics(report.metrics)) {
      toast({
        title: 'Download not available',
        description:
          'Reports that include "Investor Growth" or "Compliance Status" cannot be generated or downloaded yet. Please edit the report and deselect these metrics.',
        variant: 'destructive',
      });
      return;
    }
    try {
      setDownloadingId(report.id);
      const blob = await reportApi.download(report.id);
      const ext = getDownloadExtension(report.format || 'json');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(report.name || 'report').replace(/[^a-zA-Z0-9-_]/g, '_')}.${ext}`;
      a.click();
      URL.revokeObjectURL(url);
      toast({
        title: 'Download started',
        description: `${report.name} (${report.format || 'JSON'})`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again';
      toast({
        title: 'Download failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDeleteReport = async (id: string) => {
    try {
      const reportName = reports.find((r) => r.id === id)?.name || 'Report';
      await reportApi.delete(id);
      toast({ title: 'Report template deleted' });
      createAdminNotification({
        type: 'alert',
        title: 'Report template deleted',
        message: `"${reportName}" was removed.`,
        priority: 'low',
      });
      fetchReports();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again';
      toast({
        title: 'Failed to delete report',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const handleViewDetails = async (report: Report) => {
    try {
      setViewDetailsLoading(true);
      const full = await reportApi.getOne(report.id);
      setViewReportData(full);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again';
      toast({
        title: 'Failed to load report details',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setViewDetailsLoading(false);
    }
  };

  const closeViewDetails = () => setViewReportData(null);

  const openEditModal = async (report: Report) => {
    try {
      const full = await reportApi.getOne(report.id);
      setEditInitialData({
        name: full.name,
        type: full.type,
        schedule: full.schedule || 'On Demand',
        description: full.description || '',
        metrics: full.metrics || [],
        assets: full.assets || [],
        dateRange: full.dateRange || 'Last 30 Days',
        recipients: full.recipients || '',
        includeCharts: full.includeCharts !== false,
        format: full.format || 'PDF',
      });
      setEditReportId(report.id);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Please try again';
      toast({
        title: 'Failed to load report',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const closeEditModal = () => {
    setEditReportId(null);
    setEditInitialData(null);
  };

  const isEditOpen = !!editReportId && editInitialData !== null;

  return (
    <>
      <div className='space-y-6'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4'>
          <div>
            <h1 className='text-2xl font-bold text-foreground'>Reports</h1>
            <p className='text-muted-foreground'>
              Generate comprehensive compliance and financial reports.
            </p>
          </div>
          <Button className='gap-2' onClick={() => setIsCreateModalOpen(true)}>
            <Plus className='w-4 h-4' />
            Create Report
          </Button>
        </div>

        {loading ? (
          <div className='flex items-center justify-center py-16'>
            <Loader2 className='w-8 h-8 animate-spin text-muted-foreground' />
          </div>
        ) : (
          <div className='grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 items-stretch'>
            {reports.map((report) => (
              <div
                key={report.id}
                className='glass-card rounded-xl p-6 hover:border-primary/30 transition-all duration-300 group flex flex-col h-full min-h-0'
              >
                <div className='flex items-start justify-between mb-4 shrink-0'>
                  <div className='flex items-center gap-3'>
                    <div
                      className={`flex items-center justify-center w-12 h-12 rounded-xl ${
                        report.type === 'Financial'
                          ? 'bg-success/20 text-success'
                          : report.type === 'Compliance'
                            ? 'bg-info/20 text-info'
                            : 'bg-warning/20 text-warning'
                      }`}
                    >
                      <FileBarChart className='w-6 h-6' />
                    </div>
                    <div>
                      <h3 className='font-semibold text-foreground group-hover:text-primary transition-colors'>
                        {report.name}
                      </h3>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                          report.type === 'Financial'
                            ? 'bg-success/20 text-success border-success/30'
                            : report.type === 'Compliance'
                              ? 'bg-info/20 text-info border-info/30'
                              : 'bg-warning/20 text-warning border-warning/30'
                        }`}
                      >
                        {report.type}
                      </span>
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant='ghost'
                        size='iconSm'
                        className='opacity-0 group-hover:opacity-100 transition-opacity'
                      >
                        <MoreHorizontal className='w-4 h-4' />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align='end'>
                      <DropdownMenuLabel>Actions</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => handleViewDetails(report)}
                        disabled={viewDetailsLoading}
                      >
                        {viewDetailsLoading ? (
                          <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                        ) : (
                          <Eye className='w-4 h-4 mr-2' />
                        )}
                        View Details
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleGenerateReport(report)}
                        disabled={!!generatingId || reportHasUnsupportedMetrics(report.metrics)}
                      >
                        {generatingId === report.id ? (
                          <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                        ) : (
                          <Play className='w-4 h-4 mr-2' />
                        )}
                        Generate Now
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={() => handleDownloadReport(report)}
                        disabled={!!downloadingId || reportHasUnsupportedMetrics(report.metrics)}
                      >
                        {downloadingId === report.id ? (
                          <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                        ) : (
                          <Download className='w-4 h-4 mr-2' />
                        )}
                        Download Last
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => openEditModal(report)}>
                        <Settings className='w-4 h-4 mr-2' />
                        Edit Template
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className='text-destructive'
                        onClick={() => handleDeleteReport(report.id)}
                      >
                        <Trash2 className='w-4 h-4 mr-2' />
                        Delete Template
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>

                <div className='flex-1 min-h-0 flex flex-col'>
                  {report.description && (
                    <p className='text-sm text-muted-foreground mb-4 line-clamp-2'>
                      {report.description}
                    </p>
                  )}

                  {report.metrics && report.metrics.length > 0 && (
                    <div className='flex flex-wrap gap-1.5 mb-4'>
                      {report.metrics.slice(0, 3).map((metric) => (
                        <span
                          key={metric}
                          className='px-2 py-0.5 text-xs rounded bg-muted text-muted-foreground'
                        >
                          {metric}
                        </span>
                      ))}
                      {report.metrics.length > 3 && (
                        <span className='px-2 py-0.5 text-xs rounded bg-muted text-muted-foreground'>
                          +{report.metrics.length - 3} more
                        </span>
                      )}
                    </div>
                  )}

                  <div className='flex-1 min-h-[1rem]' aria-hidden />
                </div>

                <div className='flex items-center justify-between pt-4 border-t border-border/50 shrink-0'>
                  <div className='flex items-center gap-4 text-xs text-muted-foreground'>
                    <div className='flex items-center gap-1'>
                      <Calendar className='w-3 h-3' />
                      {report.schedule}
                    </div>
                    <div className='flex items-center gap-1'>
                      <FileText className='w-3 h-3' />
                      {report.format || 'PDF'}
                    </div>
                  </div>
                  <span className='text-xs text-muted-foreground'>
                    Last: {report.lastGenerated}
                  </span>
                </div>

                <div className='flex flex-col gap-2 mt-4 shrink-0'>
                  {reportHasUnsupportedMetrics(report.metrics) && (
                    <p className='text-xs text-amber-600 dark:text-amber-400'>
                      Generate &amp; Download are not available for reports that include Investor
                      Growth or Compliance Status. Edit the report to change metrics.
                    </p>
                  )}
                  <div className='flex gap-2'>
                    <Button
                      variant='outline'
                      size='sm'
                      className='flex-1'
                      onClick={() => handleGenerateReport(report)}
                      disabled={!!generatingId || reportHasUnsupportedMetrics(report.metrics)}
                    >
                      {generatingId === report.id ? (
                        <Loader2 className='w-3 h-3 mr-1 animate-spin' />
                      ) : (
                        <Play className='w-3 h-3 mr-1' />
                      )}
                      Generate
                    </Button>
                    <Button
                      variant='outline'
                      size='sm'
                      className='flex-1'
                      onClick={() => handleDownloadReport(report)}
                      disabled={!!downloadingId || reportHasUnsupportedMetrics(report.metrics)}
                    >
                      {downloadingId === report.id ? (
                        <Loader2 className='w-3 h-3 mr-1 animate-spin' />
                      ) : (
                        <Download className='w-3 h-3 mr-1' />
                      )}
                      Download
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {!loading && reports.length === 0 && (
          <div className='text-center py-12'>
            <p className='text-muted-foreground mb-4'>No report templates configured</p>
            <Button onClick={() => setIsCreateModalOpen(true)}>Create First Report</Button>
          </div>
        )}
      </div>

      <DetailedReportModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
        onAdd={handleAddReport}
      />

      <DetailedReportModal
        open={isEditOpen}
        onOpenChange={(open) => !open && closeEditModal()}
        onAdd={() => {}}
        onUpdate={editReportId ? (id, data) => handleUpdateReport(id, data) : undefined}
        editReportId={editReportId ?? undefined}
        initialData={editInitialData ?? undefined}
      />

      <Dialog open={!!viewReportData} onOpenChange={(open) => !open && closeViewDetails()}>
        <DialogContent className='sm:max-w-[600px] max-h-[90vh] overflow-y-auto'>
          <DialogHeader>
            <DialogTitle className='flex items-center gap-3'>
              <div className='flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary'>
                <FileBarChart className='w-5 h-5' />
              </div>
              Report Details
            </DialogTitle>
          </DialogHeader>
          {viewReportData && (
            <div className='space-y-4 py-4'>
              <div>
                <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                  Name
                </p>
                <p className='text-sm font-medium text-foreground mt-0.5'>{viewReportData.name}</p>
              </div>
              <div>
                <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                  Type
                </p>
                <p className='text-sm text-foreground mt-0.5'>{viewReportData.type}</p>
              </div>
              {viewReportData.description && (
                <div>
                  <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                    Description
                  </p>
                  <p className='text-sm text-foreground mt-0.5'>{viewReportData.description}</p>
                </div>
              )}
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                    Schedule
                  </p>
                  <p className='text-sm text-foreground mt-0.5'>{viewReportData.schedule || '—'}</p>
                </div>
                <div>
                  <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                    Format
                  </p>
                  <p className='text-sm text-foreground mt-0.5'>{viewReportData.format || '—'}</p>
                </div>
              </div>
              {viewReportData.metrics && viewReportData.metrics.length > 0 && (
                <div>
                  <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                    Metrics
                  </p>
                  <div className='flex flex-wrap gap-1.5 mt-1.5'>
                    {viewReportData.metrics.map((m) => (
                      <span
                        key={m}
                        className='px-2 py-0.5 text-xs rounded bg-muted text-muted-foreground'
                      >
                        {m}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              {viewReportData.assets && viewReportData.assets.length > 0 && (
                <div>
                  <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                    Assets
                  </p>
                  <p className='text-sm text-foreground mt-0.5'>
                    {viewReportData.assets.join(', ')}
                  </p>
                </div>
              )}
              <div className='grid grid-cols-2 gap-4'>
                <div>
                  <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                    Date range
                  </p>
                  <p className='text-sm text-foreground mt-0.5'>
                    {viewReportData.dateRange || '—'}
                  </p>
                </div>
                <div>
                  <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                    Last generated
                  </p>
                  <p className='text-sm text-foreground mt-0.5'>
                    {viewReportData.lastGenerated ?? '—'}
                  </p>
                </div>
              </div>
              {viewReportData.recipients && (
                <div>
                  <p className='text-xs font-medium text-muted-foreground uppercase tracking-wide'>
                    Email recipients
                  </p>
                  <p className='text-sm text-foreground mt-0.5'>{viewReportData.recipients}</p>
                </div>
              )}
              <div className='pt-4 border-t'>
                <Button variant='outline' onClick={closeViewDetails} className='w-full sm:w-auto'>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
